import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { keepPreviousData } from '@tanstack/react-query';
import { Search, Loader2, FileSpreadsheet, FileText } from 'lucide-react';
import dayjs from 'dayjs';
import { OrgReportsApi } from '@/services/org-reports.api';
import { OrgPortalApi } from '@/services/org.api';
import { OrgPromotionApi } from '@/services/org-promotion.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Input, Select } from '@/components/ui/Input';
import { DataTablePagination } from '@/components/data-table';
import { useDataTable } from '@/components/data-table';

/**
 * Detailed Student Attendance Report — org tier, read-only. Per-student
 * x per-date P/A/- matrix, matching the real `state-reports.
 * student-attendance-summary-report` shape. The date axis is the union
 * of every "Working Day" in the academic calendar and any date with a
 * real attendance row, across the schools in scope — same pooled-dates
 * behavior as real legacy.
 */
export function OrgDetailedStudentAttendancePage() {
  const [schoolId, setSchoolId] = useState('');
  const [classId, setClassId] = useState('');
  const [stateId, setStateId] = useState<number | ''>('');
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const dt = useDataTable({ defaultLimit: 10 });

  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, classId, stateId, startDate, endDate]);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-org-detailed-student-attendance'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: states = [] } = useQuery({
    queryKey: ['org-report-states'],
    queryFn: () => OrgReportsApi.listStates(),
  });

  const { data: classes = [] } = useQuery({
    queryKey: ['org-detailed-student-attendance-classes', schoolId],
    queryFn: () => OrgPromotionApi.classesForSchool(schoolId),
    enabled: !!schoolId,
  });

  function handleSchoolChange(v: string) {
    setSchoolId(v);
    setClassId('');
  }

  const params = {
    schoolId: schoolId || undefined,
    classId: (schoolId && classId) || undefined,
    stateId: stateId === '' ? undefined : stateId,
    startDate,
    endDate,
    page: dt.params.page,
    limit: dt.params.limit,
    search: dt.params.search,
  };

  const { data, isFetching } = useQuery({
    queryKey: ['org-detailed-student-attendance', params],
    queryFn: () => OrgReportsApi.detailedStudentAttendance(params),
    placeholderData: keepPreviousData,
  });

  const rows = data?.items ?? [];
  const dates = data?.dates ?? [];

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportDetailedStudentAttendance(format, params);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Detailed Student Attendance Report"
        description="Day-by-day present/absent matrix per student, across your organization's schools."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      {data?.truncated && (
        <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
          This organization has more students than this report can show at once — results are truncated.
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by name or admission #"
            className="w-64 pl-9"
          />
        </div>
        <Select className="!w-56" value={schoolId} onChange={(e) => handleSchoolChange(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-40"
          value={classId}
          onChange={(e) => setClassId(e.target.value)}
          disabled={!schoolId}
          title={schoolId ? undefined : 'Select a school to filter by class'}
        >
          <option value="">All Classes</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Input type="date" className="!w-40" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span className="text-sm text-slate-400">to</span>
        <Input type="date" className="!w-40" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        <Select className="!w-48" value={stateId} onChange={(e) => setStateId(e.target.value === '' ? '' : Number(e.target.value))}>
          <option value="">All states</option>
          {states.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">No.</th>
                <th className="sticky left-0 bg-slate-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Student</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">School</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Class</th>
                <th className="px-2 py-3 text-right text-xs font-semibold uppercase tracking-wider text-green-600">P</th>
                <th className="px-2 py-3 text-right text-xs font-semibold uppercase tracking-wider text-red-600">A</th>
                {dates.map((d) => (
                  <th key={d} className="px-2 py-3 text-center text-xs font-medium text-slate-400" title={d}>
                    {dayjs(d).format('D MMM')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {isFetching && !rows.length ? (
                <tr>
                  <td colSpan={6 + dates.length} className="px-4 py-12 text-center text-slate-400">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6 + dates.length} className="px-4 py-12 text-center text-slate-400">
                    No students found.
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={`${r.schoolId}-${r.studentId}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + i + 1}</td>
                    <td className="sticky left-0 bg-white px-4 py-3 font-medium text-slate-800">{r.studentName}</td>
                    <td className="px-4 py-3 text-slate-600">{r.schoolName}</td>
                    <td className="px-4 py-3 text-slate-600">{r.className}</td>
                    <td className="px-2 py-3 text-right font-medium text-green-700">{r.totalPresent}</td>
                    <td className="px-2 py-3 text-right font-medium text-red-700">{r.totalAbsent}</td>
                    {dates.map((d) => {
                      const cell = r.cells[d];
                      return (
                        <td
                          key={d}
                          className={`px-2 py-3 text-center text-xs font-semibold ${
                            cell === 'P' ? 'text-green-600' : cell === 'A' ? 'text-red-600' : 'text-slate-300'
                          }`}
                        >
                          {cell}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DataTablePagination state={dt} result={data} />
    </>
  );
}
